import { GPT_KEYS } from '@/lib/ads/constants/gpt'
import { type srpGpt } from '@/lib/ads/types/adConfig'

/**
 * Creates an object containing the search keyword target for Google Publisher Tags (GPT)
 * if the 'keywords' parameter is provided.
 *
 * @function
 * @param {string} [keywords] - Optional search keywords to be used as the value.
 * @returns {Object} An object containing the GPT key and value if 'keywords' is provided, or an empty object otherwise.
 * @example
 *
 * const searchKeywordTarget = createSearchKeywordTarget('couch');
 * console.log(searchKeywordTarget); // { key: 'kw', value: 'couch' }
 *
 * const emptyObject = createSearchKeywordTarget();
 * console.log(emptyObject); // {}
 */
export function createSearchKeywordTarget(keywords?: string) {
  if (keywords) {
    return {
      key: GPT_KEYS.SEARCH_KEYWORD_TARGET,
      value: keywords,
    }
  }
  return {}
}

/**
 * Creates an object containing the category ID for Google Publisher Tags (GPT)
 *
 * @param {number} [categoryId] - The ID of the category to use for the label.
 * @returns {Object} An object containing the category ID label key-value pair, or an empty
 * object if no category ID is provided.
 * @example
 *
 * const result = createCategoryIdLabel(123)
 * console.log(result); // { key: 'ct', value: ['123'] }
 *
 * const result = createCategoryIdLabel()
 * console.log(result); // { }
 */
export function createCategoryIdLabel(categories?: string[]) {
  if (categories?.length) {
    return {
      key: GPT_KEYS.CATEGORY_ID_LABEL,
      value: categories.reverse(),
    }
  }
  return {}
}

/**
 * Returns an object with the location ID label key-value pair if a `locationId` is provided.
 *
 * @param {number} [locationId] - Optional location ID to use for the label.
 * @returns {Object} An object containing the location ID label key-value pair if `locationId` is provided, or an empty object otherwise.
 * @example
 *
 * const locationIdLabel = createLocationIdLabel(42);
 * console.log(locationIdLabel); // { key: 'lc', value: '42' }
 *
 * const emptyObject = createLocationIdLabel();
 * console.log(emptyObject); // {}
 */
export function createLocationIdLabel(locationId?: number) {
  if (locationId || locationId === 0) {
    return {
      key: GPT_KEYS.LOCATION_ID_LABEL,
      value: `${locationId}`,
    }
  }
  return {}
}

/**
 * Adds tracking categories to an array of key-value pairs.
 *
 * @param {string[]} categories - An array of tracking categories.
 * @returns {Object[]} An array of key-value pairs representing the tracking categories.
 * @example
 *
 * const categories = ['category1', 'category2', 'category3'];
 * const trackingCategories = addTrackingCategories(categories);
 * console.log(trackingCategories);
 * // [
 * //   { key: 'l1', value: 'category1' },
 * //   { key: 'l2', value: 'category2' },
 * //   { key: 'l3', value: 'category3' }
 * // ]
 */
export function addTrackingCategories(categories: Array<string | undefined>): Array<srpGpt> {
  const keys = [
    GPT_KEYS.LEVEL_ONE_CATEGORY_LABEL,
    GPT_KEYS.LEVEL_TWO_CATEGORY_LABEL,
    GPT_KEYS.LEVEL_THREE_CATEGORY_LABEL,
  ]

  return categories.reduce((acc: Array<srpGpt>, val, index) => {
    if (val) {
      acc.push({
        key: keys[index],
        value: String(val),
      })
    }
    return acc
  }, [])
}
